import { useCallback, useEffect, useState } from "react";

import { fetchAccount, updateAccount } from "../../../data/queries/account";
import { Account, displayToEmailFlags, EmailFlagsDisplay, emailFlagsToDisplay } from "../../../types/account";
import { logError } from "../../../utils/error";
import { useAuth } from "../../auth/AuthContext";

type UseAccountResponse = {
  account: Account | null;
  emailFlagsDisplay: EmailFlagsDisplay | null;
  isLoading: boolean;
  error: Error | null;
  isSaving: boolean;
  saveError: Error | null;
  saveSuccess: boolean;
  saveAccount: (updatedAccount: Partial<Account>, updatedEmailFlags?: EmailFlagsDisplay) => Promise<void>;
  updateEmailFlags: (newEmailFlagsDisplay: EmailFlagsDisplay) => void;
  refreshAccount: () => Promise<void>;
};

export const useAccount = (): UseAccountResponse => {
  const { tenantId } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [emailFlagsDisplay, setEmailFlagsDisplay] = useState<EmailFlagsDisplay | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadAccount = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const accountData = await fetchAccount(tenantId);
      setAccount(accountData);
      setEmailFlagsDisplay(emailFlagsToDisplay(accountData.email_flags));
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load account"));
      logError("Error loading account", err);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const saveAccount = async (updatedAccount: Partial<Account>, updatedEmailFlags?: EmailFlagsDisplay) => {
    if (!account || !tenantId) {
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const emailFlags = updatedEmailFlags
        ? displayToEmailFlags(updatedEmailFlags)
        : updatedAccount.email_flags;

      const result = await updateAccount({
        id: account.id, // Use account.id from the current account
        ...updatedAccount,
        email_flags: emailFlags
      }, tenantId);

      setAccount(result);
      setEmailFlagsDisplay(emailFlagsToDisplay(result.email_flags));
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err : new Error("Failed to save account"));
      logError("Error saving account", err);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const updateEmailFlags = (newEmailFlagsDisplay: EmailFlagsDisplay) => {
    setEmailFlagsDisplay(newEmailFlagsDisplay);
  };

  return {
    account,
    emailFlagsDisplay,
    isLoading,
    error,
    isSaving,
    saveError,
    saveSuccess,
    saveAccount,
    updateEmailFlags,
    refreshAccount: loadAccount
  };
};
